// Customer.io Data Pipelines: the JavaScript "Data In" source for this site.
//
// Scope is tracking only (page, identify, track). No in-app messaging, push or
// transactional modules are enabled here; a Site ID being available does not
// change that.
//
// Configuration follows the PostHog convention in src/main.tsx: the CDP source
// write key comes from `VITE_CUSTOMERIO_CDP_WRITE_KEY`. It is a public client
// key by design and ships to the browser, but it is set only in the Vercel
// Production environment. Locally it stays empty, so dev and preview builds
// never emit, which is also enforced by the production host gate below
// (`VITE_CUSTOMERIO_ALLOW_NON_PROD=1` is the explicit escape hatch for a local
// end-to-end check against a non-production identifier).
//
// The SDK is loaded on demand with a dynamic import so it stays out of the
// critical bundle on every build where it is not configured. Calls made before
// it resolves are queued and replayed in order.

import { isProdHost } from '@/lib/analyticsHost';

type Properties = Record<string, unknown>;
type Traits = Record<string, unknown>;

/** The slice of the AnalyticsBrowser instance this module uses. */
interface CioClient {
  page: (properties?: Properties) => unknown;
  identify: (userId: string, traits?: Traits) => unknown;
  track: (event: string, properties?: Properties) => unknown;
  reset: () => unknown;
}

let client: CioClient | null = null;
let loading: Promise<void> | null = null;
let disabled = false;
let queue: Array<(c: CioClient) => void> = [];
let lastIdentifiedUserId: string | null = null;

// Public CDP source write key (workspace 236842, source 184129). It ships to the
// browser by design. Defaulted here because the live host does not read Vercel
// env; the env var still overrides it. The isProdHost gate keeps preview silent.
export const CIO_PUBLIC_WRITE_KEY = '4d50c42ad10adae2b4b0';

function writeKey(): string | undefined {
  const key = import.meta.env.VITE_CUSTOMERIO_CDP_WRITE_KEY as string | undefined;
  return key && key.trim() ? key.trim() : CIO_PUBLIC_WRITE_KEY;
}

/**
 * True when this browser should send to Customer.io: a write key is configured
 * and we are on a production host (or the non-prod escape hatch is set).
 */
export function isCustomerioEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (!writeKey()) return false;
  if (isProdHost()) return true;
  return import.meta.env.VITE_CUSTOMERIO_ALLOW_NON_PROD === '1';
}

function flush(): void {
  if (!client) return;
  const pending = queue;
  queue = [];
  for (const fn of pending) {
    try {
      fn(client);
    } catch {
      /* analytics must never break the page */
    }
  }
}

function withClient(fn: (c: CioClient) => void): void {
  if (disabled) return;
  if (client) {
    try {
      fn(client);
    } catch {
      /* ignore */
    }
    return;
  }
  if (!isCustomerioEnabled()) {
    disabled = true;
    if (import.meta.env.DEV && !writeKey()) {
      console.info('[customerio] VITE_CUSTOMERIO_CDP_WRITE_KEY is not set; Data In tracking is off for this build.');
    }
    return;
  }
  queue.push(fn);
  if (loading) return;
  loading = import('@customerio/cdp-analytics-browser')
    .then(({ AnalyticsBrowser }) => {
      // `load` returns a buffered facade immediately; the SDK replays our calls
      // once its own settings fetch completes. US region, default ingestion host.
      //
      // The two messaging plugins are pinned off. These overrides win over the
      // source's remote settings, so enabling in-app or web push later in the
      // Customer.io workspace does not silently turn them on here. Remove the
      // override for a plugin when that messaging surface is wanted.
      client = AnalyticsBrowser.load(
        { writeKey: writeKey() as string },
        {
          integrations: {
            'Customer.io In-App Plugin': { enabled: false },
            'Customer.io Web Push Plugin': { enabled: false },
          },
        },
      ) as unknown as CioClient;
      flush();
    })
    .catch((err) => {
      disabled = true;
      queue = [];
      console.warn('[customerio] SDK failed to load, continuing without Data In tracking', err);
    });
}

/**
 * Record a page view for the current URL. The SDK collects url, path, title,
 * search and referrer itself. Called from the single page-view hook
 * (src/hooks/useGAPageView.ts) so GA4 and Customer.io count the same views.
 */
export function cioPage(properties?: Properties): void {
  withClient((c) => c.page(properties));
}

/**
 * Attach the Supabase user id to the browser's anonymous profile. Repeated calls
 * for the same user within a page load are collapsed to one identify.
 */
export function cioIdentify(userId: string, traits?: Traits): void {
  if (!userId) return;
  if (lastIdentifiedUserId === userId) return;
  lastIdentifiedUserId = userId;
  withClient((c) => c.identify(userId, traits));
}

/** Send a named event with properties. */
export function cioTrack(event: string, properties?: Properties): void {
  if (!event) return;
  withClient((c) => c.track(event, properties));
}

/** Drop the identified user and anonymous id, e.g. on sign-out. */
export function cioReset(): void {
  lastIdentifiedUserId = null;
  withClient((c) => c.reset());
}

/** Test-only: clear module state between cases. */
export function __resetCustomerioForTests(): void {
  client = null;
  loading = null;
  disabled = false;
  queue = [];
  lastIdentifiedUserId = null;
}
